import { useState, useEffect, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import SEOHead from '@/components/SEOHead';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { MessageSquare, Send, Loader2, CheckCircle, Clock, ArrowLeft, Ticket, Mail, Paperclip, X, FileIcon } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useUnreadTicketReplies } from '@/hooks/useUnreadTicketReplies';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { format, Locale } from 'date-fns';
import { 
  it, de, fr, es, pt, nl, pl, ro, bg, cs, da, el, et, fi, 
  hr, hu, lt, lv, mt, sk, sl, sv, enGB
} from 'date-fns/locale';
import { z } from 'zod';

const localeMap: Record<string, Locale> = {
  it, de, fr, es, pt, nl, pl, ro, bg, cs, da, el, et, fi, 
  hr, hu, lt, lv, mt, sk, sl, sv, en: enGB, ga: enGB
};

type TicketFormData = {
  name: string;
  email: string;
  subject: string;
  message: string;
};

interface TicketReply {
  id: string;
  message: string;
  created_at: string;
  admin_id: string;
}

interface SupportTicket {
  id: string;
  subject: string;
  message: string;
  status: string;
  created_at: string;
  last_viewed_at: string | null;
  ticket_replies?: TicketReply[];
}

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const MAX_FILES = 5;
const ALLOWED_TYPES = [
  'image/jpeg', 'image/png', 'image/gif', 'image/webp',
  'application/pdf',
  'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/plain', 'text/csv'
];

export default function Support() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const { toast } = useToast();
  const { markTicketAsViewed } = useUnreadTicketReplies();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const currentLocale = localeMap[i18n.language] || enGB;
  
  const ticketSchema = useMemo(() => z.object({
    name: z.string().trim().min(1, t('support.validation.nameRequired')).max(100, t('support.validation.nameTooLong')),
    email: z.string().trim().email(t('support.validation.emailInvalid')).max(255, t('support.validation.emailTooLong')),
    subject: z.string().trim().min(1, t('support.validation.subjectRequired')).max(200, t('support.validation.subjectTooLong')),
    message: z.string().trim().min(10, t('support.validation.messageTooShort')).max(5000, t('support.validation.messageTooLong')),
  }), [t]);
  
  const [formData, setFormData] = useState<TicketFormData>({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [files, setFiles] = useState<File[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [ticketNumber, setTicketNumber] = useState('');
  const [userTickets, setUserTickets] = useState<SupportTicket[]>([]);
  const [loadingTickets, setLoadingTickets] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<keyof TicketFormData, string>>>({});
  const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(null);

  // Pre-fill email if user is logged in
  useEffect(() => {
    if (user?.email) {
      setFormData(prev => ({ ...prev, email: user.email || '' }));
    }
  }, [user]);

  // Load user's tickets if authenticated
  const loadTickets = async () => {
    if (!user) return;
    
    setLoadingTickets(true);
    try {
      const { data, error } = await supabase
        .from('support_tickets')
        .select('id, subject, message, status, created_at, last_viewed_at, ticket_replies(id, message, created_at, admin_id)')
        .order('created_at', { ascending: false })
        .limit(10);

      if (error) throw error;
      setUserTickets(data || []);
    } catch (error) {
      console.error('Error loading tickets:', error);
    } finally {
      setLoadingTickets(false);
    }
  };

  useEffect(() => {
    loadTickets();
  }, [user]);

  // Subscribe to realtime updates for ticket replies
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel(`support-ticket-replies-${Math.random().toString(36).slice(2)}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'ticket_replies'
        },
        () => {
          // Reload tickets when a new reply arrives
          loadTickets();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []);
    
    // Validate files
    const validFiles: File[] = [];
    for (const file of selectedFiles) {
      if (files.length + validFiles.length >= MAX_FILES) {
        toast({
          variant: "destructive",
          title: t('support.tooManyFiles', { defaultValue: 'Troppi file' }),
          description: t('support.maxFilesDescription', { defaultValue: `Puoi allegare massimo ${MAX_FILES} file` }),
        });
        break;
      }
      
      if (file.size > MAX_FILE_SIZE) {
        toast({
          variant: "destructive",
          title: t('support.fileTooLarge', { defaultValue: 'File troppo grande' }),
          description: t('support.maxSizeDescription', { defaultValue: `${file.name} supera i 10MB` }),
        });
        continue;
      }
      
      if (!ALLOWED_TYPES.includes(file.type)) {
        toast({
          variant: "destructive",
          title: t('support.invalidFileType', { defaultValue: 'Tipo file non supportato' }),
          description: t('support.allowedTypesDescription', { defaultValue: `${file.name} non è un tipo di file supportato` }),
        });
        continue;
      }
      
      validFiles.push(file);
    }
    
    setFiles(prev => [...prev, ...validFiles]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const uploadFiles = async (ticketId: string): Promise<string[]> => {
    if (files.length === 0) return [];
    if (!user?.id) {
      console.error('User must be authenticated to upload files');
      return [];
    }
    
    setUploadingFiles(true);
    const uploadedPaths: string[] = [];
    
    try {
      const folderPath = user.id;
      
      for (const file of files) {
        const fileExt = file.name.split('.').pop();
        const fileName = `${ticketId}/${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
        const filePath = `${folderPath}/${fileName}`;
        
        const { error: uploadError } = await supabase.storage
          .from('ticket-attachments')
          .upload(filePath, file);
        
        if (uploadError) {
          console.error('Upload error:', uploadError);
          continue;
        }
        
        uploadedPaths.push(filePath);
      }
      
      return uploadedPaths;
    } catch (error) {
      console.error('Error uploading files:', error);
      return uploadedPaths;
    } finally {
      setUploadingFiles(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    // Validate
    const result = ticketSchema.safeParse(formData);
    if (!result.success) {
      const fieldErrors: Partial<Record<keyof TicketFormData, string>> = {};
      result.error.errors.forEach(err => {
        if (err.path[0]) {
          fieldErrors[err.path[0] as keyof TicketFormData] = err.message;
        }
      });
      setErrors(fieldErrors);
      return;
    }

    setLoading(true);

    try {
      const { data, error } = await supabase.functions.invoke('create-support-ticket', {
        body: { ...formData, hasAttachments: files.length > 0 },
      });

      if (error) throw error;

      // Upload files if any
      if (files.length > 0 && data.ticketId) {
        const uploadedPaths = await uploadFiles(data.ticketId);
        
        // Update ticket with attachment paths
        if (uploadedPaths.length > 0) {
          await supabase
            .from('support_tickets')
            .update({ attachments: uploadedPaths })
            .eq('id', data.ticketId);
        }
      }

      setTicketNumber(data.ticketNumber);
      setSubmitted(true);
      
      toast({
        title: t('support.ticketCreated', { defaultValue: 'Ticket creato' }),
        description: t('support.ticketCreatedDescription', { defaultValue: 'Riceverai una conferma via email' }),
      });

      // Reload tickets if user is authenticated
      if (user) {
        const { data: newTickets } = await supabase
          .from('support_tickets')
          .select('id, subject, message, status, created_at, last_viewed_at, ticket_replies(id, message, created_at, admin_id)')
          .order('created_at', { ascending: false })
          .limit(10);
        setUserTickets(newTickets || []);
      }
    } catch (error: any) {
      console.error('Error creating ticket:', error);
      toast({
        variant: "destructive",
        title: t('common.error', { defaultValue: 'Errore' }),
        description: error.message || t('support.errorCreating', { defaultValue: 'Errore durante la creazione del ticket' }),
      });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: user?.email || '',
      subject: '',
      message: '',
    });
    setFiles([]);
    setSubmitted(false);
    setTicketNumber('');
    setErrors({});
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'open':
        return <Badge variant="secondary" className="bg-yellow-100 text-yellow-800"><Clock className="w-3 h-3 mr-1" />{t('support.statusOpen', { defaultValue: 'Aperto' })}</Badge>;
      case 'in_progress':
        return <Badge variant="secondary" className="bg-blue-100 text-blue-800"><Loader2 className="w-3 h-3 mr-1" />{t('support.statusInProgress', { defaultValue: 'In lavorazione' })}</Badge>;
      case 'resolved':
        return <Badge variant="secondary" className="bg-green-100 text-green-800"><CheckCircle className="w-3 h-3 mr-1" />{t('support.statusResolved', { defaultValue: 'Risolto' })}</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const metaTitle = t('support.metaTitle', { defaultValue: 'Supporto - TED Monitor' });
  const metaDescription = t('support.metaDescription', { defaultValue: 'Contatta il supporto TED Monitor. Apri un ticket per ricevere assistenza sul monitoraggio dei bandi europei.' });

  return (
    <DashboardLayout>
      <SEOHead title={metaTitle} description={metaDescription} path="/support" />

      <div className="max-w-4xl mx-auto">
            {/* Hero */}
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
                <MessageSquare className="w-8 h-8 text-primary" />
              </div>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground">
                {t('support.title', { defaultValue: 'Centro Assistenza' })}
              </h1>
              <p className="mt-2 text-muted-foreground">
                {t('support.subtitle', { defaultValue: 'Hai bisogno di aiuto? Apri un ticket e ti risponderemo al più presto.' })}
              </p>
            </div>

            <div className="grid gap-8 lg:grid-cols-3">
              {/* Ticket Form */}
              <div className="lg:col-span-2">
                <Card className="border-0 shadow-sm">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Ticket className="w-5 h-5" />
                      {t('support.newTicket', { defaultValue: 'Nuovo Ticket' })}
                    </CardTitle>
                    <CardDescription>
                      {t('support.formDescription', { defaultValue: 'Compila il modulo per inviare una richiesta di supporto' })}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {submitted ? (
                      <div className="text-center py-8">
                        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-4">
                          <CheckCircle className="w-8 h-8 text-green-600" />
                        </div>
                        <h3 className="text-lg font-semibold text-foreground mb-2">
                          {t('support.ticketSent', { defaultValue: 'Ticket Inviato!' })}
                        </h3>
                        <p className="text-muted-foreground mb-4">
                          {t('support.ticketSentDescription', { defaultValue: 'Il tuo ticket è stato creato con successo.' })}
                        </p>
                        <div className="bg-primary/10 rounded-lg p-4 mb-6 inline-block">
                          <p className="text-sm text-muted-foreground mb-1">{t('support.ticketNumber', { defaultValue: 'Numero Ticket' })}</p>
                          <p className="text-xl font-bold text-primary">{ticketNumber}</p>
                        </div>
                        <p className="text-sm text-muted-foreground mb-6">
                          {t('support.emailConfirmation', { defaultValue: 'Riceverai una conferma via email con i dettagli del ticket.' })}
                        </p>
                        <Button onClick={resetForm}>
                          {t('support.newTicketButton', { defaultValue: 'Apri Nuovo Ticket' })}
                        </Button>
                      </div>
                    ) : (
                      <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="grid gap-4 sm:grid-cols-2">
                          <div className="space-y-2">
                            <Label htmlFor="name">{t('support.name', { defaultValue: 'Nome' })} *</Label>
                            <Input
                              id="name"
                              placeholder={t('support.namePlaceholder', { defaultValue: 'Il tuo nome' })}
                              value={formData.name}
                              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                              className={errors.name ? 'border-destructive' : ''}
                            />
                            {errors.name && <p className="text-xs text-destructive">{errors.name}</p>}
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="email">Email *</Label>
                            <Input
                              id="email"
                              type="email"
                              placeholder="email@esempio.com"
                              value={formData.email}
                              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                              className={errors.email ? 'border-destructive' : ''}
                              disabled={!!user?.email}
                            />
                            {errors.email && <p className="text-xs text-destructive">{errors.email}</p>}
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="subject">{t('support.subject', { defaultValue: 'Oggetto' })} *</Label>
                          <Input
                            id="subject"
                            placeholder={t('support.subjectPlaceholder', { defaultValue: 'Descrivi brevemente il problema' })}
                            value={formData.subject}
                            onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                            className={errors.subject ? 'border-destructive' : ''}
                          />
                          {errors.subject && <p className="text-xs text-destructive">{errors.subject}</p>}
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="message">{t('support.message', { defaultValue: 'Messaggio' })} *</Label>
                          <Textarea
                            id="message"
                            placeholder={t('support.messagePlaceholder', { defaultValue: 'Descrivi il tuo problema in dettaglio...' })}
                            value={formData.message}
                            onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                            className={`min-h-[150px] ${errors.message ? 'border-destructive' : ''}`}
                          />
                          {errors.message && <p className="text-xs text-destructive">{errors.message}</p>}
                          <p className="text-xs text-muted-foreground text-right">
                            {formData.message.length}/5000
                          </p>
                        </div>

                        {/* File Upload Section */}
                        <div className="space-y-2">
                          <Label>{t('support.attachments', { defaultValue: 'Allegati' })}</Label>
                          <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-4 hover:border-primary/50 transition-colors">
                            <input
                              ref={fileInputRef}
                              type="file"
                              multiple
                              accept={ALLOWED_TYPES.join(',')}
                              onChange={handleFileSelect}
                              className="hidden"
                              id="file-upload"
                            />
                            <label
                              htmlFor="file-upload"
                              className="flex flex-col items-center cursor-pointer"
                            >
                              <Paperclip className="w-8 h-8 text-muted-foreground mb-2" />
                              <span className="text-sm font-medium text-foreground">
                                {t('support.dropFiles', { defaultValue: 'Clicca per allegare file' })}
                              </span>
                              <span className="text-xs text-muted-foreground mt-1">
                                {t('support.fileFormats', { defaultValue: 'PDF, immagini, documenti (max 10MB ciascuno, max 5 file)' })}
                              </span>
                            </label>
                          </div>
                          
                          {/* File List */}
                          {files.length > 0 && (
                            <div className="space-y-2 mt-3">
                              {files.map((file, index) => (
                                <div
                                  key={index}
                                  className="flex items-center justify-between p-2 bg-muted/50 rounded-lg"
                                >
                                  <div className="flex items-center gap-2 min-w-0">
                                    <FileIcon className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                                    <span className="text-sm truncate">{file.name}</span>
                                    <span className="text-xs text-muted-foreground flex-shrink-0">
                                      ({formatFileSize(file.size)})
                                    </span>
                                  </div>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    className="h-6 w-6 p-0 flex-shrink-0"
                                    onClick={() => removeFile(index)}
                                  >
                                    <X className="w-4 h-4" />
                                  </Button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        <Button type="submit" className="w-full" disabled={loading || uploadingFiles}>
                          {loading || uploadingFiles ? (
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          ) : (
                            <Send className="w-4 h-4 mr-2" />
                          )}
                          {uploadingFiles 
                            ? t('support.uploading', { defaultValue: 'Caricamento file...' })
                            : t('support.submit', { defaultValue: 'Invia Ticket' })
                          }
                        </Button>
                      </form>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Sidebar */}
              <div className="space-y-6">
                {/* Contact Info */}
                <Card className="border-0 shadow-sm">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Mail className="w-4 h-4" />
                      {t('support.directContact', { defaultValue: 'Contatto Diretto' })}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground mb-2">
                      {t('support.emailUs', { defaultValue: 'Puoi anche scriverci direttamente:' })}
                    </p>
                    <a 
                      href="mailto:support@tedmonitor.eu" 
                      className="text-primary hover:underline font-medium"
                    >
                      support@tedmonitor.eu
                    </a>
                  </CardContent>
                </Card>

                {/* User's Tickets */}
                {user && (
                  <Card className="border-0 shadow-sm">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base flex items-center gap-2">
                        <Ticket className="w-4 h-4" />
                        {t('support.yourTickets', { defaultValue: 'I Tuoi Ticket' })}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {loadingTickets ? (
                        <div className="flex justify-center py-4">
                          <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                        </div>
                      ) : userTickets.length === 0 ? (
                        <p className="text-sm text-muted-foreground text-center py-4">
                          {t('support.noTickets', { defaultValue: 'Nessun ticket aperto' })}
                        </p>
                      ) : (
                        <div className="space-y-3">
                          {userTickets.map((ticket) => (
                            <div 
                              key={ticket.id} 
                              className="p-3 bg-muted/50 rounded-lg cursor-pointer hover:bg-muted transition-colors"
                              onClick={() => {
                                const isExpanding = selectedTicket?.id !== ticket.id;
                                setSelectedTicket(isExpanding ? ticket : null);
                                if (isExpanding && ticket.ticket_replies && ticket.ticket_replies.length > 0) {
                                  markTicketAsViewed(ticket.id);
                                }
                              }}
                            >
                              <div className="flex items-start justify-between gap-2 mb-1">
                                <p className="text-sm font-medium text-foreground line-clamp-1">
                                  {ticket.subject}
                                </p>
                                {getStatusBadge(ticket.status)}
                              </div>
                              <div className="flex items-center justify-between">
                                <p className="text-xs text-muted-foreground">
                                  {format(new Date(ticket.created_at), 'dd MMM yyyy, HH:mm', { locale: currentLocale })}
                                </p>
                                {ticket.ticket_replies && ticket.ticket_replies.length > 0 && (
                                  <Badge variant="outline" className="text-xs">
                                    {ticket.ticket_replies.length} {t('support.replies', { defaultValue: 'risposte' })}
                                  </Badge>
                                )}
                              </div>
                              
                              {/* Expanded ticket detail */}
                              {selectedTicket?.id === ticket.id && (
                                <div className="mt-3 pt-3 border-t border-border space-y-3">
                                  {/* Original message */}
                                  <div className="bg-background rounded-lg p-3">
                                    <p className="text-xs font-medium text-muted-foreground mb-1">
                                      {t('support.yourMessage', { defaultValue: 'Il tuo messaggio' })}
                                    </p>
                                    <p className="text-sm text-foreground whitespace-pre-wrap">
                                      {ticket.message}
                                    </p>
                                  </div>
                                  
                                  {/* Replies */}
                                  {ticket.ticket_replies && ticket.ticket_replies.length > 0 ? (
                                    <div className="space-y-2">
                                      <p className="text-xs font-medium text-muted-foreground">
                                        {t('support.adminReplies', { defaultValue: 'Risposte del supporto' })}
                                      </p>
                                      {ticket.ticket_replies
                                        .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
                                        .map((reply) => {
                                          const isUnread = ticket.last_viewed_at 
                                            ? new Date(reply.created_at) > new Date(ticket.last_viewed_at)
                                            : true;
                                          return (
                                            <div 
                                              key={reply.id} 
                                              className={`bg-primary/5 border-l-2 rounded-r-lg p-3 ${isUnread ? 'border-destructive bg-destructive/5' : 'border-primary'}`}
                                            >
                                              <div className="flex items-center justify-between gap-2 mb-1">
                                                <p className="text-xs text-muted-foreground">
                                                  {format(new Date(reply.created_at), 'dd MMM yyyy, HH:mm', { locale: currentLocale })}
                                                </p>
                                                {isUnread && (
                                                  <Badge variant="destructive" className="text-[10px] px-1.5 py-0">
                                                    {t('support.newReply', { defaultValue: 'Nuova' })}
                                                  </Badge>
                                                )}
                                              </div>
                                              <p className="text-sm text-foreground whitespace-pre-wrap">
                                                {reply.message}
                                              </p>
                                            </div>
                                          );
                                        })}
                                    </div>
                                  ) : (
                                    <p className="text-xs text-muted-foreground italic">
                                      {t('support.noRepliesYet', { defaultValue: 'Ancora nessuna risposta' })}
                                    </p>
                                  )}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}
            </div>
          </div>
      </div>
    </DashboardLayout>
  );
}